import React, { useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Editicon,
  Deleteicon,
  Viewicon,
} from "../../../../Components/IconComponents";
import Table from "../../../../Components/UiComponents/Table/Table";
import { useTranslation } from "react-i18next";
//import { Modal, Button } from "react-bootstrap";
import {
  contactTableData,
  contactcolumnsForExport,
} from "../../../../JsonData/ContactConfig";
import { getContacts, deleteContactURL, getThemeURL, regionListURL, agencyListURL } from "../../../../Services/EndPoints";
import { Filter as FilterIcon } from "lucide-react";
import SelectComponent from "../../../../Components/UiComponents/SelectComponent/SelectComponent";
import { toast, ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";
import { getData, putDataWithBody } from "../../../../Services/Services";
import { getExportedColumnsData } from "../../../../Components/UiComponents/utils";
import { setTableData } from "../../../../CentralStore/ExportLMSTable/dataSlice";
import Confirmpopup from "../../Common/ConfirmPopup/Confirmpopup";
import StatusHandler from "../../Common/StatusHandler/StatusHandler";
import Loader from "react-spinner-loader";
import { RoleContext } from "../../../..";

const filterLabelStyle = {
  fontSize: "9.5px",
  fontWeight: 800,
  letterSpacing: ".3px",
  textTransform: "uppercase",
  color: "#98A2B3",
};

const selectStyle = {
  height: "36px", lineHeight: "34px", padding: "0 28px 0 12px", borderRadius: "8px",
  border: "1px solid #E4E7EC", fontSize: "12px", background: "#fff", color: "#1D2939",
  width: "100%", boxSizing: "border-box",
};

// Matches the table's Contact Type column (the row's `type` field).
const CONTACT_TYPE_OPTIONS = ["internal", "external"].map((v) => ({ id: v, value: v }));

const ContactTable = forwardRef((props, ref) => {
  const { roleName } = useContext(RoleContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const qs = require("qs");
  const [contactData, setContactData] = useState(contactTableData);
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [searchData, setSearchData] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const [regionsByContact, setRegionsByContact] = useState({});
  const [regionFilter, setRegionFilter] = useState("");
  const [contactTypeFilter, setContactTypeFilter] = useState("");
  const [agencyFilter, setAgencyFilter] = useState("");
  let contactRegionMap = {};
  useEffect(() => {
    getContactList();
    getFilterOptions();
  }, []);

  useEffect(() => {
    sendDataToCentralStore(searchData);
  }, [searchData]);

  const addNewHandler = () => {
    navigate("/planning/create-contact");
  };
  const getSanitizedContactList = (contactList) => {
    let contactListUnique = [];
    let contactListTempMap = [];

    contactList.forEach(function (contact) {
      if (
        !(
          contactListTempMap.indexOf(contact["type"] + "_" + contact["id"]) > -1
        )
      ) {
        contactListUnique.push(contact);
      }
      contactListTempMap.push(contact["type"] + "_" + contact["id"]);
      if (contact["region_name"]) {
        if (contactRegionMap[contact["type"] + "_" + contact["id"]]) {
          let regionArr =
            contactRegionMap[contact["type"] + "_" + contact["id"]];
          regionArr.push(contact["region_name"]);
        } else {
          contactRegionMap[contact["type"] + "_" + contact["id"]] = [
            contact["region_name"],
          ];
        }
      }
    });
    return contactListUnique;
  };
  const getContactList = async () => {
    setLoader(true);
    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    var contactList = await getData(`${getContacts}/${state_id}`);

    const regionMap = {};
    contactList.forEach((c) => {
      if (!c.region_name) return;
      const key = c.type + "_" + c.id;
      (regionMap[key] = regionMap[key] || []).push(c.region_name);
    });
    setRegionsByContact(regionMap);

    contactList = getSanitizedContactList(contactList);

    setLoader(false);
    setContactData(contactList);
  };

  // Same Region / Agency APIs the Create Contact form uses (Contact Type is
  // the form's fixed Individual/Team list).
  const getFilterOptions = async () => {
    const userProfile = await getData(getThemeURL);
    const state_id = userProfile[0]?.state_id;
    const [regionList, agencyList] = await Promise.all([
      getData(`${regionListURL}/${state_id}`),
      getData(`${agencyListURL}/${state_id}`),
    ]);
    const toOptions = (list) =>
      Array.from(new Set((Array.isArray(list) ? list : []).map((x) => x.name).filter(Boolean)))
        .map((name) => ({ id: name, value: name }));
    setRegionOptions(toOptions(regionList));
    setAgencyOptions(toOptions(agencyList));
  };

  const filteredContactData = React.useMemo(
    () =>
      (contactData || []).filter((row) => {
        if (regionFilter && !(regionsByContact[row.type + "_" + row.id] || []).includes(regionFilter)) return false;
        if (contactTypeFilter && row.type !== contactTypeFilter) return false;
        if (agencyFilter && row.agency_name !== agencyFilter) return false;
        return true;
      }),
    [contactData, regionsByContact, regionFilter, contactTypeFilter, agencyFilter]
  );

  const hasActiveFilters = regionFilter !== "" || contactTypeFilter !== "" || agencyFilter !== "";

  const clearAllFilters = () => {
    setRegionFilter("");
    setContactTypeFilter("");
    setAgencyFilter("");
  };

  const sendDataToCentralStore = (data) => {
    if (data.length > 0) {
      const columns = contactcolumnsForExport;
      dispatch(
        setTableData({
          columns,
          tableData: getExportedColumnsData(columns, data),
        })
      );
    }
  };

  // Helper function to check if a value is empty, null, or undefined
  const isEmptyValue = (value) => {
    return value === null || value === undefined || value === "" || (typeof value === 'string' && value.trim() === "");
  };

  // Helper function to display truncated text with tooltip
  const getTruncatedText = (value, maxLength = 15) => {
    if (isEmptyValue(value)) {
      return "NA";
    }
    
    const text = String(value);
    if (text.length > maxLength) {
      const truncated = text.substring(0, maxLength) + "...";
      return <span title={text}>{truncated}</span>;
    }
    return <span title={text}>{text}</span>;
  };

  const columns = React.useMemo(
    () => [
      {
        Header: t("contacttableHeaders.name"),
        accessor: "name",
        disableSortBy: true,
      },
      {
        Header: t("contacttableHeaders.phone1"),
        accessor: "phone1",
        disableSortBy: true,
      },
      {
        Header: t("contacttableHeaders.email"),
        accessor: "email",
        disableSortBy: true,
        Cell: (props) => {
          if (props.value) {
            let emailText = props.value;
            if (props.value.length > 20) {
              emailText = props.value.substring(0, 20) + "...";
            }
            return <span title={props.value}>{emailText}</span>;
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      // {
      //   Header: t("contacttableHeaders.address"),
      //   accessor: "address",
      //   Cell: (props) => {
      //     if (props.value) {
      //       let text = props.value;
      //       if (text == "") {
      //         return "NA";
      //       } else {
      //         if (props.value.length > 15) {
      //           text = props.value.substring(0, 15) + "...";
      //         }
      //         return <span title={props.value}>{text}</span>;
      //       }
      //     } else {
      //       return "NA";
      //     }
      //   },
      //   disableSortBy: true,
      // },
      {
        Header: t("contacttableHeaders.region"),
        accessor: "region_name",
        Cell: (props) => {
          if (props.value) {
            let idVal = props.row.original.type + "_" + props.row.original.id;
            if (contactRegionMap[idVal]) {
              let text = contactRegionMap[idVal].join(",");
              if (text.length > 15) {
                text = text.substring(0, 15) + "...";
                return (
                  <span title={contactRegionMap[idVal].join(",")}>{text}</span>
                );
              } else {
                return (
                  <span title={contactRegionMap[idVal].join(",")}>
                    {contactRegionMap[idVal].join(",")}
                  </span>
                );
              }
            } else {
              return "NA";
            }
          } else {
            return "NA";
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("contacttableHeaders.agencyname"),
        accessor: "agency_name",
        Cell: (props) => {
          if (props.value) {
            let text = props.value;
            if (props.value.length > 15) {
              text = props.value.substring(0, 15) + "...";
            }
            return <span title={props.value}>{text}</span>;
          } else {
            return "NA";
          }
        },

        disableSortBy: true,
      },

      {
        Header: t("contacttableHeaders.type"),
        accessor: "type",
        disableSortBy: true,
      },

      // {
      //   Header: "Contact Type",
      //   accessor: "type",
      //   disableSortBy: true,
      // },
      // {
      //   Header: "Status  ",
      //   accessor: "status",
      //   disableSortBy: true,
      //   Cell: (props) => {
      //     return <StatusHandler props={props} />;
      //   },
      // },
      {
        Header: t("contacttableHeaders.quantity"),
        accessor: "quantity",
        Cell: (props) => {
          // Handle quantity display: show 0 if value is 0, show NA if null/undefined
          if (props.value === null || props.value === undefined) {
            return "NA";
          } else {
            return props.value;
          }
        },
        disableSortBy: true,
      },
      {
        Header: t("contacttableHeaders.action"),
        disableSortBy: true,
        disableFilters: true,
        Cell: (props) => {
          const [isdelete, setIsdelete] = useState(false);
          const deletePopUp = () => {
            setIsdelete(!isdelete);
          };
          const deleteTableDataById = async (id) => {
            let data = { id: id };
            var res = await putDataWithBody(deleteContactURL, data, {});
            if (res.status == "success") {
              getContactList();
            }
            toast.success(res.msg, {
              position: toast.POSITION.TOP_CENTER,
              autoClose: 2000, // Close after 2 seconds
            });
            setIsdelete(false);
          };
          const idVal =
            props.row.original.type + "_" + props.row.original.id;
          const regionNames = contactRegionMap[idVal]
            ? contactRegionMap[idVal].join(", ")
            : props.row.original.region_name || "";
          return (
            <div className="usertable__actionsicon">
              <span
                onClick={() =>
                  navigate("/planning/contact-view", {
                    state: {
                      data: { ...props.row.original, regionNames },
                    },
                  })
                }>
                <Viewicon
                  value={props.value}
                  className="cursor-pointer me-2"
                />
              </span>
              <span
                onClick={() =>
                  navigate("/planning/create-contact", {
                    state: {
                      data: props.row.original,
                      edit: true
                    },
                  })
                }>
                {props.row.original.type == "external" && (
                  <Editicon
                    value={props.value}
                    className="cursor-pointer me-2"
                  />
                )}
              </span>
              {props.row.original.type == "external" && roleName === process.env.REACT_APP_SUPER_ADMIN && (
                <span onClick={deletePopUp}>
                  <Deleteicon
                    value={props.value}
                    className="cursor-pointer mr-1"
                  />
                </span>
              )}
              {isdelete && (
                <Confirmpopup
                  className="DeltePopup"
                  show={isdelete}
                  //   data={data}
                  title={t("deletepopuptext")}
                  message={t("deletepopupmessage")}
                  handleToggle={deletePopUp}
                  row={props.row}
                  name={props.row.original.name}
                  deleteRow={deleteTableDataById}
                  cancel={t("cancelbutton")}
                  action={t("actionbutton")}
                />
              )}
            </div>
          );
        },
      },
    ],
    [t, roleName]
  );
  return (
    <div className="create-user contact-mgmt-table">
      <style>{`
        #light .contact-mgmt-table .iccc--table th {
          background: linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%) !important;
          color: #ffffff !important;
        }
        #light .contact-mgmt-table .iccc--table tbody tr td,
        #light .contact-mgmt-table .iccc--table tbody tr th {
          border: none !important;
        }
        #light .contact-mgmt-table .iccc--table tbody tr td {
          background-color: #fff !important;
        }
        #light .contact-mgmt-table .iccc--table tbody tr:nth-child(even) td {
          background-color: #F9FAFB !important;
        }
        .contact-mgmt-table .search-form {
          position: relative;
          display: inline-flex;
          align-items: center;
        }
        .contact-mgmt-table .search-form input[type="text"] {
          width: 320px !important;
          height: 36px !important;
          line-height: 34px !important;
          padding: 0 40px 0 12px !important;
          border: 1px solid #E4E7EC !important;
          border-radius: 8px !important;
          font-size: 12px !important;
          box-sizing: border-box !important;
        }
        .contact-mgmt-table .search-form button {
          position: absolute !important;
          top: 0 !important;
          bottom: 0 !important;
          right: 0 !important;
          height: 36px !important;
          width: 36px !important;
          margin: 0 !important;
          padding: 0 !important;
          background-color: transparent !important;
          border: none !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .contact-mgmt-table .search-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .contact-mgmt-table .search-icon svg {
          display: block;
          color: #2E6DA4;
        }
      `}</style>
      <Loader
        show={loader}
        type="body"
        stack="vertical"
        message="Loading Data"
      />
      <div style={{ background: "#fff", border: "1px solid #EEF1F5", borderRadius: "10px", boxShadow: "0 1px 2px rgba(16,24,40,0.04)", padding: "10px 14px", margin: "12px 12px 12px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <span style={{ width: "20px", height: "20px", borderRadius: "6px", background: "#EBF4FA", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <FilterIcon size={11} color="#2E6DA4" />
          </span>
          <span style={{ fontSize: "12px", fontWeight: 800, color: "#2E6DA4" }}>Filters</span>
        </div>
        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 200px", maxWidth: "200px" }}>
            <label style={filterLabelStyle}>Region</label>
            <SelectComponent id="contact_region_filter" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} options={regionOptions} showPlaceHolder="Select Region" placeholderSelectable={true} style={selectStyle} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 200px", maxWidth: "200px" }}>
            <label style={filterLabelStyle}>Contact Type</label>
            <SelectComponent id="contact_type_filter" value={contactTypeFilter} onChange={(e) => setContactTypeFilter(e.target.value)} options={CONTACT_TYPE_OPTIONS} showPlaceHolder="Select Contact Type" placeholderSelectable={true} style={selectStyle} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: "0 1 240px", maxWidth: "240px" }}>
            <label style={filterLabelStyle}>Agency</label>
            <SelectComponent id="contact_agency_filter" value={agencyFilter} onChange={(e) => setAgencyFilter(e.target.value)} options={agencyOptions} showPlaceHolder="Select Agency" placeholderSelectable={true} style={selectStyle} />
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearAllFilters}
              style={{ height: "34px", padding: "0 12px", borderRadius: "8px", border: "1px solid #E4E7EC", background: "#F9FAFB", color: "#475467", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="datatable__container">
        {contactTableData && (
          <Table
            data={filteredContactData}
            columns={columns}
            numberOfRows={10}
            setSearchData={setSearchData}
            addButtonLabel={t("contactaddButtonLabel")}
            addNewHandler={addNewHandler}
            showExport={false}
            showExcelExport={true}
            exportData={filteredContactData}
            exportColumns={contactcolumnsForExport}
            exportFileName="ContactManagement"
            showFilter={false}
            showReset={false}
            onExpand={false}
            rowCount={filteredContactData.length} // Pass rowCount to the Table component
            searchPlaceholder="Search by Name, Phone Number, Email, Region, Agency or Contact Type"
            headerTableHeadStyle={{ background: "linear-gradient(120deg, #2E6DA4 0%, #3B82C4 100%)", color: "#FFFFFF" }}
            tableStyle={{
              border: "1px solid #EEF1F5",
              borderRadius: "10px",
              boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
            }}
          />
        )}
      </div>
    </div>
  );
});

export default ContactTable;
